import { supabase } from '../lib/supabase';

/**
 * Ações sobre documento jurídico — mora, advertência e distrato.
 *
 * Existem para o Painel de Cobrança e Documentos poder TRABALHAR a demanda sem
 * sair da tela. Antes o painel só sabia listar e mandar a pessoa para a tela de
 * UCs, o que contraria o pedido que originou a tela: "possa filtrar as demandas
 * e trabalhar nelas".
 *
 * ⚠️ `views/Financeiro/UnidadesList.tsx` tem gêmeas destas funções (linhas ~757
 * a ~830). Não foram extraídas de lá de propósito: a lista de UCs é usada todo
 * dia e a regra desta demanda é não mexer no que já roda. Ao fazer o corte,
 * migre a UnidadesList para cá e apague as gêmeas — duas cópias divergem.
 */

export type DocTipoEnvio = 'mora' | 'advertencia' | 'distrato';
export type DocTipoCancelavel = 'mora' | 'advertencia' | 'distrato';

const TABELA_ENVIO: Record<DocTipoEnvio, string> = {
    mora: 'notificacao_mora_logs',
    advertencia: 'comunicado_advertencia_logs',
    // O distrato passou a ter as colunas de envio na migration de 11/09/2026 —
    // antes dela era o único documento sem espelho de assinatura nem de envio.
    distrato: 'termo_cancelamento_logs',
};

/**
 * `functions.invoke` engole a causa em resposta não-2xx: devolve só
 * "Edge Function returned a non-2xx status code" e o motivo real fica em
 * `error.context`. Mesmo padrão de src/lib/gatewayResolver.ts.
 */
async function motivoReal(error: unknown, padrao: string): Promise<string> {
    const ctx = (error as { context?: { clone?: () => { json: () => Promise<{ message?: string }> } } })?.context;
    try {
        const corpo = await ctx?.clone?.().json();
        if (corpo?.message) return corpo.message;
    } catch { /* corpo não é JSON ou já foi consumido */ }
    return (error as { message?: string })?.message || padrao;
}

/** Resultado uniforme: a tela decide o toast, o serviço não conhece UI. */
export interface ResultadoAcao {
    ok: boolean;
    /** Mensagem pronta para o usuário — de sucesso ou de recusa. */
    mensagem: string;
    /** 'apagar' | 'anular' no cancelamento; a edge function é quem decide. */
    acao?: string;
}

/**
 * Registra que o PDF assinado foi enviado ao cliente.
 *
 * É este carimbo que INICIA O PRAZO — o banco calcula a partir dele (5 dias
 * úteis na mora, 15 corridos na advertência) por gatilho. Enquanto não houver
 * envio, `prazo_final` é nulo por definição, não por falta de preenchimento.
 */
export async function registrarEnvioDocumento(
    logId: number | string,
    docTipo: DocTipoEnvio,
    dataISO: string,
    userId: string | null,
): Promise<ResultadoAcao> {
    const { error } = await supabase
        .from(TABELA_ENVIO[docTipo])
        .update({ enviado_cliente_em: dataISO, enviado_cliente_por: userId })
        .eq('id', logId);

    return error
        ? { ok: false, mensagem: 'Não foi possível registrar o envio. Tente novamente.' }
        : { ok: true, mensagem: 'Envio registrado. O prazo começou a contar a partir da data informada.' };
}

/** Registra que o documento NÃO será encaminhado, com o motivo. É desfecho, não esquecimento. */
export async function registrarNaoEnvioDocumento(
    logId: number | string,
    docTipo: DocTipoEnvio,
    motivo: string,
    userId: string | null,
): Promise<ResultadoAcao> {
    const { error } = await supabase
        .from(TABELA_ENVIO[docTipo])
        .update({
            nao_enviado_cliente_em: new Date().toISOString(),
            nao_enviado_cliente_por: userId,
            nao_enviado_cliente_motivo: motivo,
        })
        .eq('id', logId);

    return error
        ? { ok: false, mensagem: 'Não foi possível registrar. Tente novamente.' }
        : { ok: true, mensagem: 'Registrado: este documento não será encaminhado ao associado.' };
}

/**
 * Cobra a assinatura do procurador (e-mail + notificação no sistema).
 *
 * A edge function recusa sozinha quando o documento já está assinado ou quando
 * outro lembrete saiu há menos de 5 minutos — por isso a recusa vem como
 * `ok: false` com mensagem, não como erro.
 */
export async function enviarLembreteAssinatura(
    logId: number | string,
    docTipo: DocTipoEnvio,
): Promise<ResultadoAcao> {
    const { data, error } = await supabase.functions.invoke('enviar-lembrete-assinatura', {
        body: { log_id: logId, doc_tipo: docTipo },
    });

    if (error) return { ok: false, mensagem: await motivoReal(error, 'Não foi possível enviar o lembrete.') };
    if (!data?.success) return { ok: false, mensagem: data?.message || 'Não foi possível enviar o lembrete.' };

    return { ok: true, mensagem: `Lembrete enviado para ${data.procurador_nome || 'o representante legal'}.` };
}

/**
 * Cancela o documento no Autentique e no VigorHub.
 *
 * Quem decide entre APAGAR (ninguém assinou) e ANULAR (já há assinatura) é a
 * edge function junto com o n8n, olhando o estado real no Autentique — nunca a
 * tela, que não tem essa informação.
 */
export async function cancelarDocumentoJuridico(
    logId: number | string,
    docTipo: DocTipoCancelavel,
    motivo: string,
): Promise<ResultadoAcao> {
    const { data, error } = await supabase.functions.invoke('cancelar-documento-juridico', {
        body: { log_id: logId, doc_tipo: docTipo, motivo },
    });

    if (error) return { ok: false, mensagem: await motivoReal(error, 'Não foi possível cancelar o documento.') };
    if (!data?.success) return { ok: false, mensagem: data?.message || 'Não foi possível cancelar o documento.' };

    return {
        ok: true,
        acao: data.acao,
        mensagem: data.acao === 'anular'
            ? 'Documento anulado no Autentique — já havia assinatura, então o registro fica no histórico.'
            : 'Documento apagado no Autentique.',
    };
}

/** De qual origem da esteira sai cada `doc_tipo`. */
export function docTipoDaOrigem(origem: string): DocTipoCancelavel | null {
    if (origem === 'notificacao_mora_logs') return 'mora';
    if (origem === 'comunicado_advertencia_logs') return 'advertencia';
    if (origem === 'termo_cancelamento_logs') return 'distrato';
    return null;
}
